import { designVariables } from "@studio3t/design-variables";
import { GenericButton, Icon } from "@studio3t/generic-components";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { editorRegionFocusRing, JsonViewer } from "@/components/JsonViewer";
import { MODAL_Z_INDEX, useModalPresence } from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { OverlayScroll } from "@/components/OverlayScroll";
import { useModalFocus } from "@/hooks/useModalFocus";
import { trapTabKey } from "./focusTrap";

const MODAL = designVariables.ui.modal;
const HEADING = designVariables.type.heading;

interface RawConfigModalProps {
  isOpen: boolean;
  // Pre-serialized JSON to display (e.g. JSON.stringify(entity, null, 2)).
  value: string;
  onClose: () => void;
  title?: string;
}

function useEscapeKey(active: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [active, onEscape]);
}

export function RawConfigModal({
  isOpen,
  value,
  onClose,
  title = "Raw configuration",
}: RawConfigModalProps) {
  const modalRef = useModalFocus(isOpen);
  const modalTitleId = useId();
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEscapeKey(isOpen, onClose);
  // Own backdrop, so it counts itself for useAnyModalOpen.
  useModalPresence(isOpen);

  // Clear a pending "Copied" reset if the modal unmounts before it fires.
  useEffect(
    () => () => {
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
    },
    [],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    if (copyResetRef.current) clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopied(false), 2000);
  }, [value]);

  if (!isOpen) return null;

  // Portalled to body: the overlay must sit above the pages' sticky
  // headers (ListPageHeader pins at z-index 10 and expects overlays to be
  // portalled) — rendered inline, the page's stacking context would let
  // the pinned header paint over the modal's top.
  return createPortal(
    <Overlay onClick={onClose} onKeyDown={trapTabKey} role="presentation">
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        tabIndex={-1}
        data-testid="raw-config-modal"
      >
        <ModalHeader>
          <ModalTitle id={modalTitleId}>{title}</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={onClose}
            aria-label="Close modal"
          />
        </ModalHeader>

        <ModalBody>
          {/* The viewer's Copy menu portals to the body: it must beat the
              overlay below or it opens underneath, invisible. */}
          <JsonViewer value={value} floatingZIndex={MODAL_Z_INDEX + 1} />
        </ModalBody>

        <ModalFooter>
          <Separator />
          <FooterActions>
            <FooterLeft>
              <GenericButton
                text={copied ? "Copied" : "Copy"}
                type="secondary"
                icon={copied ? Icon.Confirm : Icon.Copy}
                iconPosition="leading"
                onClick={handleCopy}
              />
            </FooterLeft>
            <FooterRight>
              {/* macOS button order — the dismissing action left of the
                  accepting one. */}
              <GenericButton text="Cancel" type="secondary" onClick={onClose} />
              <GenericButton text="OK" type="primary" onClick={onClose} />
            </FooterRight>
          </FooterActions>
        </ModalFooter>
      </Modal>
    </Overlay>,
    document.body,
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${(p) => p.theme.modalBackdropBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${MODAL_Z_INDEX};
`;

const Modal = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.modalBackground};
  border-radius: ${MODAL.corner}px;
  width: 100%;
  /* Figma nodes 13421-57415/57418 (Modal header/footer) — 680px wide. */
  max-width: 680px;
  max-height: 85vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  /* Focused programmatically on open; inner controls keep their own ring. */
  outline: 0;
`;

const ModalHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${MODAL.header.paddingh}px ${MODAL.paddingw}px;
  border-bottom: 1px solid ${(p) => p.theme.semanticBorderLight};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${HEADING.h4.fontSize}px;
  font-weight: ${HEADING.h4.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
`;

// The modal body scrolls with the app's custom scrollbars (the JSON viewer grows
// to content; OverlayScrollbars manages the body overflow). The editor sits
// flush — no padding around the code zone (Figma Modal body) — framed by the
// header's bottom border and the footer separator. The keyboard focus ring is
// drawn over the body, the box the eye sees (see editorRegionFocusRing).
const ModalBody = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  ${editorRegionFocusRing}
`;

const ModalFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  background: ${(p) => p.theme.modalFooterBackground};
  border-bottom-left-radius: ${MODAL.corner}px;
  border-bottom-right-radius: ${MODAL.corner}px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${(p) => p.theme.semanticBorderLight};
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${MODAL.footer.gap}px;
  width: 100%;
  padding: ${MODAL.footer.paddingh}px ${MODAL.paddingw}px;
`;

const FooterLeft = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${MODAL.footer.gap}px;
`;
